/**
 * Onboarding : création du profil entreprise.
 *
 * Principe appliqué partout dans ce formulaire : **ne jamais forcer une
 * information inventée**. Chaque liste comporte « Inconnu », et le texte
 * d'aide explique que l'application signalera les obligations qu'elle ne peut
 * pas déterminer plutôt que d'en affirmer une à tort.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { businessApi } from "../api/endpoints";
import { AuthLayout } from "../components/Layout";
import {
  Alert,
  Button,
  SelectField,
  TextField,
} from "../components/ui";
import { useSession } from "../hooks/SessionContext";
import { useDocumentTitle, useMutation, useResource } from "../hooks/useApi";

const STEPS = ["Identité", "Activité", "Obligations"] as const;

interface FormState {
  trade_name: string;
  legal_name: string;
  siren: string;
  siret: string;
  ape_code: string;
  created_on: string;
  activity_started_on: string;
  legal_form: string;
  tax_regime: string;
  activity_nature: string;
  main_activity_label: string;
  urssaf_periodicity: string;
  vat_regime: string;
  income_tax_option: string;
  acre: string;
  has_employees: string;
  has_dedicated_bank_account: string;
  has_professional_insurance: string;
  intracommunity_operations: string;
  regulated_activity: string;
  city: string;
  postal_code: string;
  address_line1: string;
}

const INITIAL: FormState = {
  trade_name: "",
  legal_name: "",
  siren: "",
  siret: "",
  ape_code: "",
  created_on: "",
  activity_started_on: "",
  legal_form: "sole_trader",
  tax_regime: "micro",
  activity_nature: "unknown",
  main_activity_label: "",
  urssaf_periodicity: "unknown",
  vat_regime: "unknown",
  income_tax_option: "unknown",
  acre: "unknown",
  has_employees: "unknown",
  has_dedicated_bank_account: "unknown",
  has_professional_insurance: "unknown",
  intracommunity_operations: "unknown",
  regulated_activity: "unknown",
  city: "",
  postal_code: "",
  address_line1: "",
};

export function OnboardingPage() {
  useDocumentTitle("Configuration de votre entreprise");
  const navigate = useNavigate();
  const { refresh } = useSession();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>(INITIAL);

  const options = useResource(() => businessApi.options(), []);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const payload = useMemo(() => {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(form)) {
      if (value === "") continue;
      out[key] = value;
    }
    return out;
  }, [form]);

  const create = useMutation(() => businessApi.create(payload), {
    onSuccess: async () => {
      await refresh();
      navigate("/", { replace: true });
    },
  });

  if (options.loading) {
    return (
      <AuthLayout title="Configuration de votre entreprise">
        <p className="text-muted">Chargement du formulaire…</p>
      </AuthLayout>
    );
  }

  const o = options.data;
  const canContinue = step > 0 || form.trade_name.trim().length > 0;

  return (
    <AuthLayout
      title="Configurer votre entreprise"
      intro="Ces informations déterminent les obligations qui vous concernent. Vous pourrez toutes les modifier ensuite."
    >
      <ol className="progress-steps">
        {STEPS.map((label, index) => (
          <li
            key={label}
            className="progress-steps__item"
            data-state={
              index < step ? "done" : index === step ? "current" : "todo"
            }
          >
            {label}
          </li>
        ))}
      </ol>

      <Alert tone="info" title="Une information inconnue peut rester inconnue">
        Si vous n'avez pas une information sous la main, laissez « Inconnu ».
        L'application signalera les obligations qu'elle ne peut pas déterminer,
        plutôt que d'en affirmer une à tort.
      </Alert>

      {create.error && (
        <Alert tone="critical" title="Enregistrement impossible">
          {create.error.message}
        </Alert>
      )}

      <form
        className="mt-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (step < STEPS.length - 1) setStep(step + 1);
          else void create.run();
        }}
        noValidate
      >
        {step === 0 && (
          <>
            <TextField
              label="Nom commercial"
              required
              autoFocus
              value={form.trade_name}
              onChange={(event) => set("trade_name", event.target.value)}
              error={create.fieldErrors.trade_name}
              hint="Le nom sous lequel vous exercez. Exemple : ArkVibes.app"
            />
            <TextField
              label="Raison sociale"
              optional
              value={form.legal_name}
              onChange={(event) => set("legal_name", event.target.value)}
              error={create.fieldErrors.legal_name}
            />
            <TextField
              label="SIREN"
              optional
              inputMode="numeric"
              value={form.siren}
              onChange={(event) => set("siren", event.target.value)}
              error={create.fieldErrors.siren}
              hint="9 chiffres. Laissez vide si vous ne l'avez pas encore."
            />
            <TextField
              label="SIRET"
              optional
              inputMode="numeric"
              value={form.siret}
              onChange={(event) => set("siret", event.target.value)}
              error={create.fieldErrors.siret}
              hint="14 chiffres. Nécessaire pour certaines démarches."
            />
            <TextField
              label="Date de création de l'entreprise"
              type="date"
              optional
              value={form.created_on}
              onChange={(event) => set("created_on", event.target.value)}
              error={create.fieldErrors.created_on}
              hint="Elle conditionne les formalités de première année."
            />
            <TextField
              label="Date de début d'activité"
              type="date"
              optional
              value={form.activity_started_on}
              onChange={(event) => set("activity_started_on", event.target.value)}
              error={create.fieldErrors.activity_started_on}
              hint="Point de départ des déclarations périodiques."
            />
          </>
        )}

        {step === 1 && o && (
          <>
            <SelectField
              label="Forme juridique"
              options={o.legal_forms}
              value={form.legal_form}
              onChange={(event) => set("legal_form", event.target.value)}
              error={create.fieldErrors.legal_form}
            />
            <SelectField
              label="Régime fiscal"
              options={o.tax_regimes}
              value={form.tax_regime}
              onChange={(event) => set("tax_regime", event.target.value)}
              error={create.fieldErrors.tax_regime}
            />
            <SelectField
              label="Nature de l'activité"
              options={o.activity_natures}
              value={form.activity_nature}
              onChange={(event) => set("activity_nature", event.target.value)}
              error={create.fieldErrors.activity_nature}
              hint="Détermine notamment les seuils applicables et le registre des achats."
            />
            <TextField
              label="Activité principale"
              optional
              value={form.main_activity_label}
              onChange={(event) => set("main_activity_label", event.target.value)}
              hint="Exemple : édition de logiciels et d'applications."
            />
            <TextField
              label="Code APE"
              optional
              value={form.ape_code}
              onChange={(event) => set("ape_code", event.target.value)}
            />
            <TextField
              label="Ville"
              optional
              value={form.city}
              onChange={(event) => set("city", event.target.value)}
            />
          </>
        )}

        {step === 2 && o && (
          <>
            <SelectField
              label="Périodicité de déclaration Urssaf"
              options={o.urssaf_periodicities}
              value={form.urssaf_periodicity}
              onChange={(event) => set("urssaf_periodicity", event.target.value)}
              hint="Elle détermine la fréquence de vos déclarations de chiffre d'affaires."
            />
            <SelectField
              label="Régime de TVA"
              options={o.vat_regimes}
              value={form.vat_regime}
              onChange={(event) => set("vat_regime", event.target.value)}
            />
            <SelectField
              label="Option fiscale"
              options={o.income_tax_options}
              value={form.income_tax_option}
              onChange={(event) => set("income_tax_option", event.target.value)}
            />
            <SelectField
              label="Bénéficiez-vous de l'ACRE ?"
              options={o.tri_states}
              value={form.acre}
              onChange={(event) => set("acre", event.target.value)}
            />
            <SelectField
              label="Avez-vous un compte bancaire dédié à l'activité ?"
              options={o.tri_states}
              value={form.has_dedicated_bank_account}
              onChange={(event) =>
                set("has_dedicated_bank_account", event.target.value)
              }
            />
            <SelectField
              label="Avez-vous une assurance professionnelle ?"
              options={o.tri_states}
              value={form.has_professional_insurance}
              onChange={(event) =>
                set("has_professional_insurance", event.target.value)
              }
            />
            <SelectField
              label="Réalisez-vous des opérations intracommunautaires ?"
              options={o.tri_states}
              value={form.intracommunity_operations}
              onChange={(event) =>
                set("intracommunity_operations", event.target.value)
              }
              hint="Achats ou prestations de services avec d'autres pays de l'Union européenne."
            />
            <SelectField
              label="Votre activité est-elle réglementée ?"
              options={o.tri_states}
              value={form.regulated_activity}
              onChange={(event) => set("regulated_activity", event.target.value)}
            />
            <SelectField
              label="Avez-vous des salariés ?"
              options={o.tri_states}
              value={form.has_employees}
              onChange={(event) => set("has_employees", event.target.value)}
            />
          </>
        )}

        <div className="btn-group mt-5">
          {step > 0 && (
            <Button variant="ghost" type="button" onClick={() => setStep(step - 1)}>
              Précédent
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            loading={create.pending}
            disabled={!canContinue}
          >
            {step < STEPS.length - 1
              ? "Continuer"
              : "Créer mon profil et générer ma checklist"}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
